import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true); // true on first load

  // ── Read token from URL (after Google OAuth redirect) ──
  // Google redirects back to /dashboard?token=xxx
  // We grab it, store it, then clean the URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenFromUrl = params.get('token');
    const isOAuthRedirect = !!tokenFromUrl;

    if (tokenFromUrl) {
      localStorage.setItem('gla_token', tokenFromUrl);
      // Remove token from URL so it's not visible
      window.history.replaceState({}, '', '/');
    }

    fetchMe().then((loggedInUser) => {
      if (isOAuthRedirect && loggedInUser) {
        // Navigate to the correct place after OAuth login
        window.location.replace(
          loggedInUser.profileComplete ? '/app/dashboard' : '/complete-profile'
        );
      }
    });
  }, []);

  // ── Fetch current user from /api/auth/me ───────────────
  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem('gla_token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data.user);
      return data.user; // returned so OAuth redirect can use it
    } catch {
      // Token expired or invalid — clear it
      localStorage.removeItem('gla_token');
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Called after profile is completed ─────────────────
  const updateUser = (updatedUser) => {
    setUser(updatedUser);
  };

  // ── Logout ─────────────────────────────────────────────
  const logout = () => {
    localStorage.removeItem('gla_token');
    setUser(null);
    window.location.href = '/login';
  };

  const isAdmin           = user?.isAdmin === true;
  const isProfileComplete = user?.profileComplete === true;

  return (
    <AuthContext.Provider
      value={{ user, loading, isAdmin, isProfileComplete, updateUser, logout, fetchMe }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};